// Modal.js
import React, { useState } from "react";
import "../../components/Modal.css";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import GeneriqueModal from "components/GeneriqueModal";
import ButtonCancel from "components/buttons/ButtonCancel";
import ButtonGreen from "components/buttons/ButtonGreen";
import { useCreateComptePaiementMutation } from "actions/compte_paiement/mutations";
import { useGetOperateursQuery } from "actions/operateurs/queries";
import ModalAjouterOperateur from "./ModalAjouterOperateur";  // ✨ Nouveau import

const TYPE_OPTIONS = [
  { value: 'monnaie_electronique', label: 'Paiement Mobile', icon: '📱', color: '#1b9cf2', bg: '#EFF8FF', sub: 'Wave, Orange Money…' },
  { value: 'banque',               label: 'Banque',          icon: '🏦', color: '#003087', bg: '#EFF2FF', sub: 'Ecobank, SGBS, UBA…' },
  { value: 'caisse',               label: 'Caisse',          icon: '💵', color: '#068f7a', bg: '#EFFAF7', sub: 'Argent liquide'       },
];

const ModalCodeJournal = ({ isOpen, onClose, onDossierCreated, dossier_id }) => {
  const [creationComptePaiement, { isLoading }] = useCreateComptePaiementMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = React.useRef(false);

  const [step,         setStep        ] = useState(1);    // 1 ou 2
  const [selectedType, setSelectedType] = useState(null);
  const [selectedOp,   setSelectedOp  ] = useState(null);
  const [showAddOperateur, setShowAddOperateur] = useState(false);  // ✨ Nouveau state

  // 🔍 DEBUG: Log pour vérifier dossier_id
  console.log('🔍 Modal - dossier_id:', dossier_id);
  console.log('🔍 Modal - step:', step);

  // ✨ Skip la query si dossier_id n'existe pas
  const { data: operateursRaw, refetch: refetchOperateurs, isLoading: isLoadingOperateurs, error } = useGetOperateursQuery(
    { dossier_id },
    { skip: !dossier_id }  // Ne faire la requête que si dossier_id existe
  );
  
  // 🔍 DEBUG: Log pour vérifier la réponse
  console.log('🔍 Modal - operateursRaw:', operateursRaw);
  console.log('🔍 Modal - isLoadingOperateurs:', isLoadingOperateurs);
  console.log('🔍 Modal - error:', error);
  
  const tousLesOperateurs = Array.isArray(operateursRaw)
    ? operateursRaw
    : (operateursRaw?.data || operateursRaw?.results || []);

  const operateursFiltres = selectedType
    ? tousLesOperateurs.filter(op => op.type === selectedType.value && op.is_active !== false)
    : [];

  const { handleSubmit, reset } = useFormContext();

  const handleReset = () => { setStep(1); setSelectedType(null); setSelectedOp(null); };
  const handleClose = () => { handleReset(); onClose(); };

  // ── Passer à l'étape 2 ────────────────────────────────────────────
  const handleNextStep = () => {
    if (!selectedType) { toast.error("Sélectionnez un type de compte."); return; }
    setStep(2);
  };

  // ── Soumission finale (étape 2) ───────────────────────────────────
  const onSubmit = async (data) => {
    if (isSubmittingRef.current || isLoading || isSubmitting) return;

    if (selectedType.value !== 'caisse' && !selectedOp) {
      toast.error("Sélectionnez un opérateur.");
      return;
    }

    const comptePaiement = {
      ...data,
      compte:          selectedOp ? selectedOp.nom : 'Caisse',
      source_paiement: selectedType.value,
      operateur:       selectedOp ? selectedOp.id : null,
      dossier_id,
    };

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await creationComptePaiement(comptePaiement).unwrap();
      toast.success("Création du compte de paiement réussie !");
      reset();
      handleReset();
      onDossierCreated();
    } catch (error) {
      console.log(error);
      toast.error("Erreur lors de la création");
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };


  return (
    <>
      <GeneriqueModal
        className="modal-overlay"
        onClose={handleClose}
        title={"Ajouter un compte monétaire"}
        isOpen={isOpen}
      >
        <form className="codeJournal-form" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>

          {/* ── Fil d'Ariane ──────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <StepDot num={1} active={step >= 1} done={step > 1} label="Catégorie" />
            <div style={{
              flex: 1, height: 2, borderRadius: 2, transition: 'background .3s',
              backgroundColor: step > 1 ? '#068f7a' : '#E5E7EB',
            }} />
            <StepDot num={2} active={step >= 2} done={false} label="Opérateur" />
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/* ÉTAPE 1 - Choix de la catégorie                             */}
          {/* ════════════════════════════════════════════════════════════ */}
          {step === 1 && (
            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-label" style={{ marginBottom: 10, display: 'block' }}>
                Quel type de compte souhaitez-vous créer ?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {TYPE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSelectedType(opt)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${selectedType?.value === opt.value ? opt.color : '#E5E7EB'}`,
                      backgroundColor: selectedType?.value === opt.value ? opt.bg : '#FAFAFA',
                      transition: 'all .2s', textAlign: 'left', width: '100%',
                    }}
                  >
                    <span style={{ fontSize: 24 }}>{opt.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{opt.sub}</div>
                    </div>
                    {selectedType?.value === opt.value && (
                      <span style={{ color: opt.color, fontWeight: 700, fontSize: 16 }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════ */}
          {/* ÉTAPE 2 - Sélection de l'opérateur + création               */}
          {/* ════════════════════════════════════════════════════════════ */}
          {step === 2 && (
            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Badge type sélectionné */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                backgroundColor: selectedType.bg,
                border: `1px solid ${selectedType.color}44`,
                color: selectedType.color,
                borderRadius: 20, padding: '3px 12px',
                fontSize: 12, fontWeight: 600, marginBottom: 14,
              }}>
                {selectedType.icon} {selectedType.label}
              </div>

              {selectedType.value === 'caisse' ? (
                /* Caisse - pas d'opérateur */
                <div style={{
                  display: 'flex', background: "#000",flexDirection: 'row', justifyContent:'center', alignItems: 'center', gap: 12,
                  padding: 14, borderRadius: 10,
                  border: '2px solid #068f7a', backgroundColor: '#EFFAF7',
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 8,
                    backgroundColor: '#068f7a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
                  }}>💵</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Caisse</div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>Code comptable 571xxx</div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: '#068f7a', fontWeight: 700, fontSize: 18 }}>✓</span>
                </div>
              ) : operateursFiltres.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0', fontSize: 13 }}>
                  Aucun opérateur disponible.<br />
                  <code style={{ fontSize: 11 }}>runscript seed_operateurs</code>
                </div>
              ) : (
                <div style={{
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                    gap: 12, width: '100%',
                  }}>
                  <label className="form-label" style={{ marginBottom: 10, display: 'block', width: '100%', textAlign: 'center' }}>
                    Sélectionnez l&apos;opérateur :
                  </label>
                  <div className="operateurs-grid">
                    {operateursFiltres.map(op => (
                      <OperateurCard
                        key={op.id}
                        operateur={op}
                        selected={selectedOp?.id === op.id}
                        onSelect={() => setSelectedOp(op)}
                      />
                    ))}
                  </div>
                  
                  {/* ✨ Bouton pour ajouter un opérateur personnalisé */}
                  <div style={{ marginTop: 12, textAlign: 'center', width: '100%' }}>
                    <button
                      type="button"
                      onClick={() => setShowAddOperateur(true)}
                      style={{
                        fontSize: 12,
                        color: selectedType.color,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        padding: '8px 0',
                      }}
                    >
                      + {selectedType.value === 'banque' ? 'Ma banque' : 'Mon opérateur'} n&apos;est pas dans la liste
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Footer ────────────────────────────────────────────────── */}
          <div className="modal-footer">
            {step === 1 ? (
              <>
                <ButtonCancel text={"Annuler"} onClick={handleClose} />
                <ButtonGreen
                  text={"Suivant →"}
                  type={"button"}
                  onClick={handleNextStep}
                  disabled={!selectedType}
                />
              </>
            ) : (
              <>
                <ButtonCancel text={"← Retour"} onClick={() => { setStep(1); setSelectedOp(null); }} />
                <ButtonGreen
                  text={isLoading || isSubmitting ? "Création..." : "Créer le compte"}
                  type={"submit"}
                  loading={isLoading || isSubmitting}
                  disabled={isLoading || isSubmitting || (selectedType.value !== 'caisse' && !selectedOp)}
                />
              </>

            )}
          </div>

        </form>
      </GeneriqueModal>
      
      {/* ✨ Modal d'ajout d'opérateur personnalisé */}
      {showAddOperateur && (
        <ModalAjouterOperateur
          isOpen={showAddOperateur}
          onClose={() => setShowAddOperateur(false)}
          type={selectedType?.value}
          dossier_id={dossier_id}
          onSuccess={(newOperateur) => {
            // Rafraîchir la liste des opérateurs
            refetchOperateurs();
            // Sélectionner automatiquement le nouvel opérateur
            setSelectedOp(newOperateur);
            // Fermer le modal d'ajout
            setShowAddOperateur(false);
            toast.success("Vous pouvez maintenant utiliser ce nouvel opérateur !");
          }}
        />
      )}
    </>
  );
};

// ── Indicateur d'étape ─────────────────────────────────────────────────
function StepDot({ num, active, done, label }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{
        width: 30, height: 30, borderRadius: '50%',
        backgroundColor: active ? '#068f7a' : '#E5E7EB',
        color: active ? '#fff' : '#9CA3AF',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 13, transition: 'background .3s',
      }}>
        {done ? '✓' : num}
      </div>
      <span style={{ fontSize: 10, fontWeight: active ? 600 : 400, color: active ? '#068f7a' : '#9CA3AF' }}>
        {label}
      </span>
    </div>
  );
}

// ── Carte opérateur ────────────────────────────────────────────────────
function OperateurCard({ operateur, selected, onSelect }) {
  const [imgError, setImgError] = useState(false);
  const couleur = operateur.couleur || '#068f7a';

  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center', 
        gap: 8,
        padding: '12px 8px', 
        borderRadius: 10, 
        cursor: 'pointer',
        border: `2px solid ${selected ? couleur : '#E5E7EB'}`,
        backgroundColor: selected ? `${couleur}11` : '#FAFAFA',
        transition: 'all .2s', 
        minHeight: '110px',
        position: 'relative',
        boxShadow: selected ? `0 0 0 3px ${couleur}33` : 'none',
      }}
    >
      {selected && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          width: 20, height: 20, borderRadius: '50%',
          backgroundColor: couleur,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 11, fontWeight: 700,
        }}>✓</div>
      )}

      {operateur.logo && !imgError ? (
        <img
          src={operateur.logo}
          alt={operateur.nom}
          onError={() => setImgError(true)}
          style={{ 
            width: 50, 
            height: 50, 
            objectFit: 'contain', 
            borderRadius: 6 
          }}
        />
      ) : (
        <div style={{
          width: 50, height: 50, borderRadius: 8,
          backgroundColor: couleur,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 20,
        }}>
          {operateur.nom.charAt(0).toUpperCase()}
        </div>
      )}

      <span style={{
        fontSize: 11, 
        fontWeight: selected ? 700 : 500,
        color: selected ? couleur : '#374151',
        textAlign: 'center', 
        lineHeight: 1.3,
        wordBreak: 'break-word',
        maxWidth: '100%',
      }}>
        {operateur.nom}
      </span>
    </button>
  );
}

export default ModalCodeJournal;